export const MENTION_TRIGGER_PATTERN = /(^|\s)@([^\s@]*)$/;

const isWordChar = (char) => Boolean(char) && (/\d/.test(char) || char.toLowerCase() !== char.toUpperCase());

const hasTokenBoundaries = (text, start, end) => {
  const before = start === 0 ? "" : text[start - 1];
  return (!before || /[\s([{"']/.test(before)) && !isWordChar(text[end]);
};

/**
 * Normalises mentions from the picker, the store or persisted history to
 * `{ employee_id, label }`, dropping anything without both.
 */
export const normalizeMentions = (mentions) => {
  const seen = new Set();
  return (Array.isArray(mentions) ? mentions : []).reduce((list, item) => {
    const employeeId = item?.employee_id ?? item?.employeeId;
    const label = typeof item?.label === "string" ? item.label.trim() : "";
    if (employeeId == null || employeeId === "" || !label || seen.has(String(employeeId))) {
      return list;
    }
    seen.add(String(employeeId));
    list.push({ employee_id: employeeId, label });
    return list;
  }, []);
};

/**
 * Finds every `@label` of a picked mention that sits on word boundaries.
 * Longer labels claim their range first so "@Jo Smith" wins over "@Jo".
 */
export const findMentionRanges = (text, mentions) => {
  const source = typeof text === "string" ? text : "";
  const ranges = [];
  [...normalizeMentions(mentions)]
    .sort((a, b) => b.label.length - a.label.length)
    .forEach((mention) => {
      const token = `@${mention.label}`;
      let index = source.indexOf(token);
      while (index !== -1) {
        const end = index + token.length;
        const overlaps = ranges.some((range) => index < range.end && end > range.start);
        if (!overlaps && hasTokenBoundaries(source, index, end)) {
          ranges.push({ start: index, end, mention });
        }
        index = source.indexOf(token, index + 1);
      }
    });
  return ranges.sort((a, b) => a.start - b.start);
};

/**
 * Splits text into ordered plain / mention segments for rendering.
 *
 * @returns {Array<{ start: number, text: string, mention: object|null }>}
 */
export const segmentTextWithMentions = (text, mentions) => {
  const source = typeof text === "string" ? text : "";
  const segments = [];
  let cursor = 0;
  findMentionRanges(source, mentions).forEach((range) => {
    if (range.start > cursor) {
      segments.push({ start: cursor, text: source.slice(cursor, range.start), mention: null });
    }
    segments.push({
      start: range.start,
      text: source.slice(range.start, range.end),
      mention: range.mention,
    });
    cursor = range.end;
  });
  if (cursor < source.length) {
    segments.push({ start: cursor, text: source.slice(cursor), mention: null });
  }
  return segments;
};

/**
 * Returns the open `@query` ending at the caret, or null. An `@` inside a word
 * (jo@acme.com) never triggers, nor does a caret inside a picked token.
 */
export const findMentionTrigger = (text, caret, mentions = []) => {
  const source = typeof text === "string" ? text : "";
  const position = Number.isInteger(caret) ? caret : source.length;
  const match = MENTION_TRIGGER_PATTERN.exec(source.slice(0, position));
  if (!match) {
    return null;
  }
  const start = position - match[2].length - 1;
  const insideToken = findMentionRanges(source, mentions).some(
    (range) => start >= range.start && start < range.end
  );
  return insideToken ? null : { start, end: position, query: match[2] };
};
